import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Link, useLocation, Navigate } from "react-router-dom";
import {
  getMeetings,
  getMeeting,
  createMeeting,
  joinMeetingWithBot,
  leaveMeetingWithBot,
  getBotStatus,
  syncBotStatus,
  getTranscript,
  getAnalysis,
  getMom,
  exportMom,
  getAnalyticsOverview,
  getAnalyticsTrends,
  getSpeakerAnalytics,
  getDecisionsActionsAnalytics,
  validateMeetingUrl,
  createSampleMeeting,
  deleteMeeting,
  rerunAnalysis,
  login,
  register,
  logout,
  isAuthenticated,
  getCurrentUser,
  updateProfile,
  changePassword,
  uploadBotAvatar,
  deleteBotAvatar,
  initiateAuth,
  verifyAuth,
  resendCode,
  googleAuthCallback,
  sendMomEmail,
  assignActionTask,
  getTasks,
  createTask,
  updateTask,
  deleteTask,
  createTaskFromAction,
} from "@/services/api";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  Check,
  Upload,
  Play,
  Pause,
  ChevronLeft,
  Download,
  RefreshCw,
  Trash2,
  Bot,
  Target,
  ListChecks,
  Search,
  MoreHorizontal,
  Bell,
  Settings,
  StopCircle,
  BarChart3,
  TrendingUp,
  FileText,
  Mic,
  PieChart,
  LogOut,
  User,
  Lock,
  Mail,
  Building,
  ChevronRight,
  Eye,
  EyeOff,
  Link2,
  UserPlus,
  ImageIcon,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// ============== AUTH CONTEXT ==============
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getCurrentUser());
  const [loading, setLoading] = useState(false);

  const handleLogin = async (email, password) => {
    const response = await login(email, password);
    setUser(response.user);
    return response;
  };

  const handleRegister = async (email, password, name, company) => {
    const response = await register(email, password, name, company);
    setUser(response.user);
    return response;
  };

  const handleLogout = () => {
    logout();
    setUser(null);
  };

  const updateUser = (newUser) => {
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  // New: Direct login with token (for magic code & Google auth)
  const loginWithToken = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      login: handleLogin,
      register: handleRegister,
      logout: handleLogout,
      updateUser,
      loginWithToken
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// ============== PROTECTED ROUTE ==============
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// ============== HEADER COMPONENT ==============
const Header = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { user, isAuthenticated, logout: handleLogout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    handleLogout();
    navigate('/login');
    toast.success('Çıkış yapıldı');
  };

  // Don't show header on login/register pages
  if (currentPath === '/login' || currentPath === '/register') {
    return null;
  }

  return (
    <header className="header" data-testid="header">
      <div className="header-content">
        {/* Logo - Notellio */}
        <Link to="/" className="logo" data-testid="logo">
          <img src="/logo.png" alt="Notellio" className="notellio-header-icon" />
        </Link>

        {/* Navigation Tabs */}
        <div className="nav-tabs">
          <Link
            to="/"
            className={`nav-tab ${currentPath === "/" ? "active" : ""}`}
            data-testid="nav-meetings"
          >
            Toplantılar
          </Link>
          <Link
            to="/analytics"
            className={`nav-tab ${currentPath === "/analytics" ? "active" : ""}`}
            data-testid="nav-analytics"
          >
            Analitik
          </Link>
          <Link
            to="/tasks"
            className={`nav-tab ${currentPath === "/tasks" ? "active" : ""}`}
            data-testid="nav-tasks"
          >
            Tasklar
          </Link>
        </div>

        {/* Right Side */}
        <div className="header-right">
          <div className="header-icon">
            <Bell size={20} />
            <div className="notification-dot" />
          </div>
          
          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="user-avatar" data-testid="user-menu">
                  {user.name?.[0]?.toUpperCase() || 'U'}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b">
                  <p className="font-medium">{user.name}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Ayarlar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="outline" size="sm" onClick={() => navigate('/login')}>
              Giriş Yap
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

// ============== MEETING CARD COMPONENT ==============
const MeetingCard = ({ meeting, onClick }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: "Bekliyor", className: "status-pending", icon: Clock },
      bot_joining: { label: "Bot Katılıyor", className: "status-processing", icon: Bot },
      recording: { label: "Kayıt Alınıyor", className: "status-processing", icon: Play },
      processing: { label: "İşleniyor", className: "status-processing", icon: RefreshCw },
      completed: { label: "Tamamlandı", className: "status-completed", icon: CheckCircle },
      failed: { label: "Hata", className: "status-failed", icon: AlertCircle },
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`status-badge ${config.className}`}>
        <Icon size={14} className={status === "processing" ? "animate-spin" : ""} />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "--";
    const mins = Math.floor(seconds / 60);
    return `${mins} dk`;
  };

  return (
    <div
      className="meeting-card"
      onClick={() => onClick(meeting.id)}
      data-testid={`meeting-card-${meeting.id}`}
    >
      <div className="meeting-card-header">
        <h3 className="meeting-title">{meeting.title}</h3>
        {getStatusBadge(meeting.status)}
      </div>
      <p className="meeting-date">{formatDate(meeting.created_at)}</p>
      <div className="meeting-meta">
        {meeting.participant_count > 0 && (
          <span className="meeting-meta-item">
            <Users size={16} />
            {meeting.participant_count} katılımcı
          </span>
        )}
        <span className="meeting-meta-item">
          <Clock size={16} />
          {formatDuration(meeting.duration_seconds)}
        </span>
        {meeting.analysis_ready && (
          <span className="meeting-meta-item" style={{ color: "#2E7D32" }}>
            <CheckCircle size={16} />
            Analiz Hazır
          </span>
        )}
      </div>
    </div>
  );
};

// ============== DASHBOARD PAGE ==============
const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meetingUrl, setMeetingUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 5,
    total: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });

  const fetchData = useCallback(async (page = 1) => {
    try {
      const data = await getMeetings(page, 5);
      setMeetings(data.meetings || []);
      setPagination(data.pagination || {
        page: 1,
        per_page: 5,
        total: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(pagination.page);
    const interval = setInterval(() => fetchData(pagination.page), 10000);
    return () => clearInterval(interval);
  }, [fetchData, pagination.page]);

  const handlePageChange = (newPage) => {
    setLoading(true);
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchData(newPage);
  };

  const handleSendBot = async () => {
    if (!meetingUrl.trim()) {
      toast.error("Lütfen toplantı linkini girin");
      return;
    }

    setIsSubmitting(true);
    try {
      // Extract title from URL or use default
      const title = `Toplantı - ${new Date().toLocaleDateString("tr-TR")}`;
      
      const meeting = await createMeeting({
        title: title,
        platform: "teams",
        meeting_url: meetingUrl,
      });

      await joinMeetingWithBot(meeting.id);
      toast.success("Bot toplantıya katılıyor!");
      setMeetingUrl("");
      fetchData(1);
      navigate(`/meeting/${meeting.id}`);
    } catch (error) {
      console.error("Failed to send bot:", error);
      const errorMsg = error.response?.data?.detail || "Bot gönderilemedi";
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateDemo = async () => {
    try {
      const result = await createSampleMeeting();
      toast.success("Demo toplantı oluşturuldu!");
      fetchData(1);
      navigate(`/meeting/${result.meeting_id}`);
    } catch (error) {
      console.error("Failed to create demo:", error);
      toast.error("Demo oluşturulamadı");
    }
  };

  if (loading && meetings.length === 0) {
    return (
      <div className="page-container">
        <Skeleton className="h-16 w-64 mb-4" />
        <Skeleton className="h-6 w-96 mb-12" />
        <Skeleton className="h-64 max-w-2xl mx-auto rounded-2xl mb-12" />
        <Skeleton className="h-8 w-48 mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container animate-fade-in" data-testid="dashboard">
      {/* Passive Welcome - Secondary Info */}
      <div className="welcome-passive">
        <span>Hoş geldiniz{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</span>
      </div>

      {/* Hero Section - MAIN FOCUS */}
      <div className="hero-focused">
        <h1 className="hero-title-main">
          Toplantılarınızı
          <span className="hero-highlight"> Akıllı Harekete Dönüştürün</span>
        </h1>
        <p className="hero-subtitle-single">
          AI asistanınız toplantıları sizin için özetler ve aksiyonları çıkarır.
        </p>

        {/* MAIN CARD - Primary Action */}
        <div className="action-card-premium" data-testid="main-card">
          <input
            type="text"
            placeholder="Toplantı linkini buraya yapıştırın"
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
            className="action-input-premium"
            data-testid="meeting-url-input"
          />
          <button
            onClick={handleSendBot}
            disabled={isSubmitting || !meetingUrl.trim()}
            className={`action-button-premium ${meetingUrl.trim() ? 'active' : ''}`}
            data-testid="send-bot-btn"
          >
            {isSubmitting ? "Ekleniyor..." : "Toplantıya Asistanı Ekle"}
          </button>
        </div>

        {/* Microcopy - Trust */}
        <p className="trust-text">Teams, Zoom ve Google Meet desteklenir</p>

        {/* 3 Steps - PASSIVE/BACKGROUND */}
        <div className="steps-passive">
          <span><Link2 size={14} /> Linki ekleyin</span>
          <span className="step-divider">→</span>
          <span><UserPlus size={14} /> Asistan katılsın</span>
          <span className="step-divider">→</span>
          <span><FileText size={14} /> Özet alın</span>
        </div>
      </div>

      {/* Recent Meetings */}
      <div className="section-header">
        <h2 className="section-title">Son Toplantılar</h2>
        {pagination.total > 0 && (
          <span className="text-sm text-gray-500">
            Toplam {pagination.total} toplantı
          </span>
        )}
      </div>

      {meetings.length === 0 ? (
        <div className="empty-state-premium">
          <div className="empty-illustration">
            <div className="empty-circles">
              <div className="circle circle-1"></div>
              <div className="circle circle-2"></div>
              <div className="circle circle-3"></div>
            </div>
            <div className="empty-icon-wrapper">
              <Calendar size={32} />
            </div>
          </div>
          <h3 className="empty-title">İlk Toplantınızı Kaydedin</h3>
          <p className="empty-description">
            Yukarıya bir toplantı linki yapıştırın ve<br />
            <strong>30 saniyede</strong> otomatik notlar almaya başlayın
          </p>
        </div>
      ) : (
        <>
          <div className="meeting-list">
            {meetings.map((meeting) => (
              <MeetingCard
                key={meeting.id}
                meeting={meeting}
                onClick={(id) => navigate(`/meeting/${id}`)}
              />
            ))}
          </div>
          
          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="pagination-controls" data-testid="pagination">
              <button
                className="pagination-arrow"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.has_prev}
              >
                <ChevronLeft size={18} />
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((pageNum) => {
                  // Show first, last, current, and neighbors
                  const showPage = pageNum === 1 || 
                    pageNum === pagination.total_pages || 
                    Math.abs(pageNum - pagination.page) <= 1;
                  
                  const showEllipsis = pageNum === 2 && pagination.page > 3 ||
                    pageNum === pagination.total_pages - 1 && pagination.page < pagination.total_pages - 2;
                  
                  if (!showPage && !showEllipsis) return null;
                  
                  if (showEllipsis && !showPage) {
                    return <span key={pageNum} className="pagination-ellipsis">...</span>;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-circle ${pagination.page === pageNum ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                className="pagination-arrow"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.has_next}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============== MEETING DETAIL PAGE ==============
const MeetingDetail = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [mom, setMom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");  // Default to summary instead of transcript
  const [searchQuery, setSearchQuery] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLeavingBot, setIsLeavingBot] = useState(false);
  
  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  // Task assignment dialog state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedActionIndex, setSelectedActionIndex] = useState(null);
  const [assigneeName, setAssigneeName] = useState("");
  const [assigneeEmail, setAssigneeEmail] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  
  // Track which actions are already added as tasks
  const [actionTaskMap, setActionTaskMap] = useState({});  // {actionIndex: taskId}
  
  // Collapsible topics state
  const [expandedTopics, setExpandedTopics] = useState({});  // {topicIndex: boolean}
  const [showParkingLot, setShowParkingLot] = useState(false);  // Diğer Notlar visibility
  
  // MoM Edit Mode State
  const [isEditingMom, setIsEditingMom] = useState(false);
  const [editedMomContent, setEditedMomContent] = useState('');
  const [originalMomContent, setOriginalMomContent] = useState('');
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const editableDivRef = useRef(null);  // Ref for contentEditable div
  const isEditingInitializedRef = useRef(false);  // Track if HTML was set
  
  // Tasks state - derived from action items
  const [tasks, setTasks] = useState([]);
  
  // Initialize tasks from action items
  useEffect(() => {
    if (analysis?.action_items) {
      setTasks(analysis.action_items.map((action, idx) => ({
        id: action.id || `task-${idx}`,
        task: action.task,
        owner: action.owner,
        deadline: action.deadline,
        priority: action.priority || 'medium',
        status: action.status || 'pending',
        confidence: action.confidence,
        exact_quote: action.exact_quote
      })));
    }
  }, [analysis]);
  
  const toggleTaskStatus = (taskId) => {
    setTasks(prev => prev.map(task => {
      if (task.id === taskId) {
        return {
          ...task,
          status: task.status === 'completed' ? 'pending' : 'completed'
        };
      }
      return task;
    }));
  };
  
  // MoM Edit Functions
  const handleEditMom = () => {
    // Use HTML content directly for editing
    const htmlContent = mom?.content_html || '';
    
    setOriginalMomContent(htmlContent);
    setEditedMomContent(htmlContent);
    setIsEditingMom(true);
    isEditingInitializedRef.current = false; // Reset flag
  };
  
  // Set initial HTML only once when edit mode starts
  useEffect(() => {
    if (isEditingMom && editableDivRef.current && editedMomContent && !isEditingInitializedRef.current) {
      editableDivRef.current.innerHTML = editedMomContent;
      editableDivRef.current.focus();
      isEditingInitializedRef.current = true;
    }
  }, [isEditingMom, editedMomContent]);
  
  const handleCancelEditMom = () => {
    setIsEditingMom(false);
    setEditedMomContent('');
    setOriginalMomContent('');
    isEditingInitializedRef.current = false;
  };
  
  // Handle contentEditable input without losing cursor position
  const handleMomInput = () => {
    if (editableDivRef.current) {
      setEditedMomContent(editableDivRef.current.innerHTML);
    }
  };
  
  const handleSaveMom = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/${meetingId}/mom/edit`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          edited_content: editedMomContent
        })
      });
      
      if (!response.ok) {
        throw new Error('MoM kaydedilemedi');
      }
      
      toast.success('MoM başarıyla güncellendi!');
      
      // Update local mom state with edited HTML
      setMom(prev => ({
        ...prev,
        content_html: editedMomContent,
        is_edited: true
      }));
      
      setIsEditingMom(false);
    } catch (error) {
      console.error('Save MoM error:', error);
      toast.error('MoM güncellenirken hata oluştu');
    }
  };
  
  const handleFindReplace = () => {
    if (!findText) {
      toast.error('Aranacak metni girin');
      return;
    }
    
    if (!editableDivRef.current) return;
    
    // Get current HTML from ref
    const currentHTML = editableDivRef.current.innerHTML;
    
    // Create a temporary div to work with HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = currentHTML;
    
    // Get all text nodes and replace
    const textNodes = [];
    const getTextNodes = (node) => {
      if (node.nodeType === 3) { // Text node
        textNodes.push(node);
      } else {
        for (let child of node.childNodes) {
          getTextNodes(child);
        }
      }
    };
    getTextNodes(tempDiv);
    
    let count = 0;
    const regex = new RegExp(findText, 'gi');
    
    textNodes.forEach(node => {
      if (node.nodeValue && regex.test(node.nodeValue)) {
        const matches = node.nodeValue.match(regex);
        count += matches ? matches.length : 0;
        node.nodeValue = node.nodeValue.replace(regex, replaceText);
      }
    });
    
    // Update the contentEditable div
    editableDivRef.current.innerHTML = tempDiv.innerHTML;
    setEditedMomContent(tempDiv.innerHTML);
    
    if (count > 0) {
      toast.success(`${count} değişiklik yapıldı`);
    } else {
      toast.info('Değiştirilecek metin bulunamadı');
    }
    
    setShowFindReplace(false);
    setFindText('');
    setReplaceText('');
  };

  const fetchMeetingData = useCallback(async () => {
    try {
      const meetingData = await getMeeting(meetingId);
      setMeeting(meetingData);

      if (meetingData.transcript_ready) {
        const transcriptData = await getTranscript(meetingId);
        setTranscript(transcriptData);
      }

      if (meetingData.analysis_ready) {
        const [analysisData, momData] = await Promise.all([
          getAnalysis(meetingId),
          getMom(meetingId).catch(() => null),
        ]);
        setAnalysis(analysisData);
        setMom(momData);
      }
    } catch (error) {
      console.error("Failed to fetch meeting:", error);
      toast.error("Toplantı bilgileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  // Sync bot status when meeting is active
  const syncBotStatusData = useCallback(async () => {
    if (!meeting?.bot_id) return;
    if (meeting?.status === 'completed' || meeting?.status === 'failed') return;
    
    try {
      const syncResult = await syncBotStatus(meetingId);
      console.log('Bot sync result:', syncResult);
      
      // Refresh meeting data if status changed or transcript ready
      if (syncResult.meeting_status !== meeting?.status || 
          syncResult.transcript_ready !== meeting?.transcript_ready ||
          syncResult.analysis_ready) {
        fetchMeetingData();
      }
    } catch (error) {
      console.error('Failed to sync bot status:', error);
    }
  }, [meetingId, meeting?.bot_id, meeting?.status, meeting?.transcript_ready, fetchMeetingData]);

  useEffect(() => {
    fetchMeetingData();
    
    // Poll for status updates when meeting is active
    const interval = setInterval(() => {
      if (meeting?.status === "bot_joining" || meeting?.status === "recording") {
        syncBotStatusData();
      } else if (meeting?.status === "processing") {
        fetchMeetingData();
      }
    }, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [fetchMeetingData, syncBotStatusData, meeting?.status]);

  // PDF Preview Modal State
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfBase64, setPdfBase64] = useState(null);
  
  const handleExport = async (format) => {
    try {
      if (format === 'pdf') {
        // Use browser's native print to PDF
        window.print();
        toast.success('Print dialog açıldı - "Save as PDF" seçebilirsiniz');
      } else if (format === 'word') {
        // Word export not available
        toast.error('Word export şu anda kullanılamıyor');
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Dışa aktarma başarısız: " + error.message);
    }
  };
  
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link kopyalandı! Yeni tarayıcı sekmesinde açın.');
    } catch (e) {
      toast.error('Kopyalama başarısız, linki manuel seçip kopyalayın.');
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Bu toplantıyı silmek istediğinizden emin misiniz?")) {
      try {
        await deleteMeeting(meetingId);
        toast.success("Toplantı silindi");
        navigate("/");
      } catch (error) {
        toast.error("Silme başarısız");
      }
    }
  };

  const handleRerunAnalysis = async () => {
    try {
      await rerunAnalysis(meetingId);
      toast.success("Analiz yeniden başlatıldı");
      fetchMeetingData();
    } catch (error) {
      toast.error("Analiz başlatılamadı");
    }
  };

  const handleLeaveBot = async () => {
    console.log('[handleLeaveBot] Function called');
    console.log('[handleLeaveBot] Meeting:', meeting);
    console.log('[handleLeaveBot] Meeting ID:', meetingId);
    console.log('[handleLeaveBot] Bot ID:', meeting?.bot_id);
    
    if (!meeting?.bot_id) {
      console.log('[handleLeaveBot] ERROR: No bot_id found');
      toast.error("Bu toplantıda aktif bot yok");
      return;
    }
    
    // Verify token exists before making critical request
    const token = localStorage.getItem('token');
    console.log('[handleLeaveBot] Token exists:', !!token);
    
    if (!token) {
      console.log('[handleLeaveBot] ERROR: No token');
      toast.error("Oturum süreniz dolmuş, lütfen tekrar giriş yapın");
      setTimeout(() => window.location.href = '/login', 1500);
      return;
    }
    
    // Skip window.confirm due to iframe sandbox restrictions
    // Directly proceed with leaving
    console.log('[handleLeaveBot] Starting leave process (no confirm due to sandbox)...');
    setIsLeavingBot(true);
    
    try {
      console.log('[handleLeaveBot] Calling leaveMeetingWithBot API...');
      const result = await leaveMeetingWithBot(meetingId);
      console.log('[handleLeaveBot] API Success:', result);
      toast.success("Bot toplantıdan çıkıyor, analiz başlıyor...");
      
      // Poll for status updates
      setTimeout(() => fetchMeetingData(), 2000);
    } catch (error) {
      console.error("[handleLeaveBot] API Error:", error);
      console.error("[handleLeaveBot] Error response:", error.response);
      console.error("[handleLeaveBot] Error status:", error.response?.status);
      console.error("[handleLeaveBot] Error data:", error.response?.data);
      
      if (error.response?.status === 401) {
        toast.error("Oturum süreniz dolmuş, lütfen tekrar giriş yapın");
      } else {
        toast.error("Bot çıkarılamadı: " + (error.response?.data?.detail || error.message));
      }
    } finally {
      console.log('[handleLeaveBot] Process complete, resetting state');
      setIsLeavingBot(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailRecipients.trim()) {
      toast.error("Lütfen en az bir e-posta adresi girin");
      return;
    }

    const recipients = emailRecipients.split(',').map(email => email.trim()).filter(Boolean);
    
    if (recipients.length === 0) {
      toast.error("Geçerli e-posta adresi bulunamadı");
      return;
    }

    setIsSendingEmail(true);
    try {
      await sendMomEmail(meetingId, recipients, emailSubject || null, emailBody || null);
      toast.success("Toplantı notu e-posta ile gönderildi!");
      setShowEmailDialog(false);
      setEmailRecipients("");
      setEmailSubject("");
      setEmailBody("");
    } catch (error) {
      console.error("Failed to send email:", error);
      toast.error(error.response?.data?.detail || "E-posta gönderilemedi");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleAssignTask = async () => {
    if (!assigneeName.trim()) {
      toast.error("Lütfen bir isim girin");
      return;
    }

    setIsAssigning(true);
    try {
      await assignActionTask(
        meetingId,
        selectedActionIndex,
        assigneeName,
        assigneeEmail || null,
        null, // status - don't change
        null  // notes
      );
      
      toast.success(`Görev ${assigneeName}'e atandı!`);
      setShowAssignDialog(false);
      setAssigneeName("");
      setAssigneeEmail("");
      setSelectedActionIndex(null);
      
      // Refresh meeting data
      fetchMeetingData();
    } catch (error) {
      console.error("Failed to assign task:", error);
      toast.error(error.response?.data?.detail || "Görev atanamadı");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleAddTaskFromAction = async (actionIndex) => {
    try {
      const result = await createTaskFromAction(meetingId, actionIndex);
      
      if (result.already_exists) {
        toast.info("Bu aksiyon zaten task olarak eklendi");
      } else {
        toast.success("Task oluşturuldu! Tasklar sayfasından görüntüleyebilirsiniz");
        
        // Update action task map
        setActionTaskMap(prev => ({
          ...prev,
          [actionIndex]: result.task.id
        }));
      }
    } catch (error) {
      console.error("Failed to create task:", error);
      toast.error(error.response?.data?.detail || "Task oluşturulamadı");
    }
  };

  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const getSpeakerColor = (speakerId) => {
    const colors = ["#5E5CE6", "#32ADE6", "#30D158", "#FF9F0A", "#FF375F", "#BF5AF2"];
    const index = parseInt(speakerId?.replace(/\D/g, "") || "0") % colors.length;
    return colors[index];
  };

  const filteredSegments = transcript?.segments?.filter((seg) =>
    searchQuery
      ? seg.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
        seg.speaker_name.toLowerCase().includes(searchQuery.toLowerCase())
      : true
  );

  if (loading) {
    return (
      <div className="page-container">
        <Skeleton className="h-12 w-48 mb-6" />
        <div className="two-column-layout">
          <Skeleton className="h-full rounded-2xl" />
          <Skeleton className="h-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="page-container">
        <div className="card-main">
          <div className="empty-state">
            <div className="empty-state-icon">
              <AlertCircle size={28} />
            </div>
            <h3 className="empty-state-title">Toplantı bulunamadı</h3>
            <Button onClick={() => navigate("/")} className="mt-4">
              Ana Sayfaya Dön
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "summary", label: "Özet", disabled: !analysis },
    { id: "decisions", label: `Kararlar (${analysis?.decisions?.length || 0})`, disabled: !analysis },
    { id: "actions", label: `Aksiyonlar (${analysis?.action_items?.length || 0})`, disabled: !analysis },
    { id: "mom", label: "MoM", disabled: !mom },
  ];

  return (
    <div className="page-container animate-fade-in" data-testid="meeting-detail">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/")}
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
            data-testid="back-btn"
          >
            <ChevronLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{meeting.title}</h1>
            <p className="text-gray-500 text-sm">
              {new Date(meeting.created_at).toLocaleDateString("tr-TR", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
              {" • "}
              {meeting.participant_count || 0} katılımcı
              {meeting.duration_seconds && ` • ${Math.floor(meeting.duration_seconds / 60)} dakika`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Botu Kaldır butonu kaldırıldı - Auto-recovery sistemi otomatik hallediyor */}
          {meeting.analysis_ready && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="export-btn">
                  <Download size={16} />
                  Dışa Aktar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleExport("pdf")}>
                  <FileText className="mr-2 h-4 w-4" />
                  PDF İndir
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("word")}>
                  <FileText className="mr-2 h-4 w-4" />
                  Word İndir
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("markdown")} disabled>
                  <FileText className="mr-2 h-4 w-4 opacity-50" />
                  Markdown (Yakında)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal size={20} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={handleRerunAnalysis}>
                <RefreshCw className="mr-2" size={16} />
                Analizi Yeniden Çalıştır
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDelete} className="text-red-600">
                <Trash2 className="mr-2" size={16} />
                Sil
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Bot Status - when bot is joining or recording */}
      {(meeting.status === "bot_joining" || meeting.status === "recording") && (
        <div className="card-main mb-6 p-6 bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bot className={`text-green-600 ${meeting.status === "recording" ? "animate-pulse" : ""}`} size={24} />
              <div>
                <p className="font-medium text-green-900">
                  {meeting.status === "bot_joining" ? "Bot Toplantıya Katılıyor..." : "Kayıt Yapılıyor"}
                </p>
                <p className="text-sm text-green-700">
                  {meeting.status === "bot_joining" 
                    ? "Bot bekleme odasında. Lütfen toplantıda botu kabul edin." 
                    : "Bot toplantıyı kayıt ediyor ve ses algılıyor."}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {meeting.status === "recording" && (
                <div className="flex items-center gap-2 text-green-600">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                  <span className="text-sm font-medium">CANLI</span>
                </div>
              )}
              {/* Botu Kaldır Butonu */}
              {meeting.status === "recording" && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleLeaveBot}
                  disabled={isLeavingBot}
                  className="ml-2"
                  data-testid="leave-bot-btn"
                >
                  {isLeavingBot ? (
                    <>
                      <RefreshCw className="animate-spin mr-2" size={14} />
                      Çıkıyor...
                    </>
                  ) : (
                    <>
                      <LogOut size={14} className="mr-2" />
                      Botu Kaldır
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Processing State */}
      {meeting.status === "processing" && (
        <div className="card-main mb-6 p-6 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3">
            <RefreshCw className="animate-spin text-blue-600" size={20} />
            <div>
              <p className="font-medium text-blue-900">Analiz ediliyor...</p>
              <p className="text-sm text-blue-700">
                Multi-agent AI sistemi transcript&apos;i işliyor. Bu birkaç dakika sürebilir.
              </p>
            </div>
          </div>
          <Progress className="mt-3" value={66} />
        </div>
      )}

      {/* Tabs */}
      <div className="tab-list-alt mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            className={`tab-item-alt ${activeTab === tab.id ? "active" : ""}`}
            disabled={tab.disabled}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === "mom" ? (
        /* MoM Full Width Layout */
        <div className="card-main">
          {mom ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Toplantı Notları (MoM)</h3>
                <div className="flex gap-2">
                  {!isEditingMom ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEditMom}
                        className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Düzenle
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowEmailDialog(true)}
                        data-testid="email-mom-btn"
                      >
                        <Mail size={16} className="mr-2" />
                        E-posta Gönder
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport("pdf")}
                        data-testid="export-pdf-btn"
                      >
                        <Download size={16} className="mr-2" />
                        PDF İndir
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport("word")}
                        data-testid="export-word-btn"
                      >
                        <Download size={16} className="mr-2" />
                        Word İndir
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowFindReplace(true)}
                        className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                        Tümünü Değiştir
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelEditMom}
                      >
                        İptal
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveMom}
                        className="bg-green-600 text-white hover:bg-green-700"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Kaydet
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {isEditingMom && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    💡 <strong>Düzenleme Modu:</strong> MoM içeriğini istediğiniz gibi değiştirebilirsiniz. 
                    "Tümünü Değiştir" ile toplu düzeltme yapabilirsiniz.
                  </p>
                </div>
              )}
              
              {/* MoM Content - Editable or View Mode */}
              {isEditingMom ? (
                <div 
                  ref={editableDivRef}
                  className="border border-blue-300 rounded-lg p-6 bg-white mom-preview-fullwidth prose max-w-none"
                  contentEditable={true}
                  suppressContentEditableWarning={true}
                  onInput={handleMomInput}
                  style={{ 
                    minHeight: '600px',
                    outline: 'none',
                    cursor: 'text'
                  }}
                />
              ) : (
                <div
                  className="mom-preview-fullwidth prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: mom.content_html }}
                  data-testid="mom-preview"
                />
              )}
              
              {/* Find & Replace Modal */}
              {showFindReplace && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                    <div className="flex items-center justify-between p-4 border-b">
                      <h3 className="text-lg font-semibold">Tümünü Değiştir</h3>
                      <button 
                        onClick={() => setShowFindReplace(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="p-6 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Bulunacak Metin
                        </label>
                        <input
                          type="text"
                          value={findText}
                          onChange={(e) => setFindText(e.target.value)}
                          placeholder="Örn: Doksogon"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Yerine Konacak Metin
                        </label>
                        <input
                          type="text"
                          value={replaceText}
                          onChange={(e) => setReplaceText(e.target.value)}
                          placeholder="Örn: Doxagon"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          onClick={() => setShowFindReplace(false)}
                          className="flex-1"
                        >
                          İptal
                        </Button>
                        <Button
                          onClick={handleFindReplace}
                          className="flex-1 bg-purple-600 text-white hover:bg-purple-700"
                        >
                          Değiştir
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-12 text-center">
              <RefreshCw className="mx-auto mb-4 text-gray-400 animate-spin" size={32} />
              <p className="text-gray-500">Toplantı notları hazırlanıyor...</p>
            </div>
          )}
        </div>
      ) : (
      <div className="two-column-layout">
        {/* Main Content */}
        <div className="panel">
          {/* Summary Tab - Enhanced with tl;dv style organization */}
          {activeTab === "summary" && analysis && (
            <ScrollArea className="panel-content">
              <div className="space-y-6">
                {/* Executive Summary */}
                {analysis.analysis_failed || (!analysis.executive_summary && (!analysis.organized_topics || analysis.organized_topics.length === 0) && (!analysis.decisions || analysis.decisions.length === 0) && (!analysis.action_items || analysis.action_items.length === 0)) ? (
                  <div className="bg-amber-50 border border-amber-200 p-8 rounded-xl text-center" data-testid="analysis-failed-card">
                    <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
                      <RefreshCw className="text-amber-600" size={28} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Analiz Henüz Tamamlanmadı</h3>
                    <p className="text-gray-600 mb-5 max-w-md mx-auto">
                      Bu toplantının yapay zekâ analizi oluşturulamadı. Transkript hazır ise "Yeniden Analiz Et" butonuna tıklayarak tekrar deneyebilirsiniz.
                    </p>
                    <Button onClick={handleRerunAnalysis} className="gap-2" data-testid="retry-analysis-btn">
                      <RefreshCw size={16} />
                      Yeniden Analiz Et
                    </Button>
                  </div>
                ) : (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="text-blue-600" size={20} />
                    <h3 className="text-lg font-semibold text-gray-800">Yönetici Özeti</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {analysis.executive_summary}
                  </p>
                </div>
                )}

                {/* Organized Topics (tl;dv style) */}
                {analysis.organized_topics?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <ListChecks className="text-purple-600" size={20} />
                      Konu Başlıkları
                    </h3>
                    <div className="space-y-4">
                      {analysis.organized_topics.map((topic, idx) => (
                        <div key={idx} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between mb-3">
                            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                              <span className="text-purple-500">▼</span>
                              {topic.title}
                            </h4>
                            {topic.speakers?.length > 0 && (
                              <div className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                                <Users size={12} />
                                {topic.speakers.join(', ')}
                              </div>
                            )}
                          </div>
                          
                          {/* Key Points */}
                          {topic.key_points?.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs font-semibold text-gray-500 mb-2">📝 Önemli Noktalar:</p>
                              <ul className="space-y-2 ml-2">
                                {topic.key_points.map((point, pidx) => {
                                  const isObject = typeof point === 'object';
                                  const text = isObject ? point.text : point;
                                  
                                  return (
                                    <li key={pidx} className="flex items-start gap-2 text-sm text-gray-700">
                                      <span className="text-purple-500 font-bold mt-0.5">•</span>
                                      <span className="flex-1">{text}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                          
                          {/* Collapsible Conversations */}
                          {topic.related_segments && topic.related_segments.length > 0 && transcript?.segments && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <button
                                onClick={() => setExpandedTopics(prev => ({...prev, [idx]: !prev[idx]}))}
                                className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-purple-600 transition-colors w-full"
                              >
                                <span className={`transform transition-transform ${expandedTopics[idx] ? 'rotate-90' : ''}`}>▶</span>
                                💬 Konuşmalar ({topic.related_segments.length} segment)
                              </button>
                              
                              {expandedTopics[idx] && (
                                <div className="mt-3 space-y-2 bg-gray-50 rounded-lg p-3 max-h-96 overflow-y-auto">
                                  {topic.related_segments.map((segIdx, sidx) => {
                                    const segment = transcript.segments[segIdx];
                                    if (!segment) return null;
                                    
                                    return (
                                      <div key={sidx} className="text-sm border-l-2 border-purple-300 pl-3 py-1">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="font-medium text-purple-700">{segment.speaker_name}</span>
                                          <span className="text-xs text-gray-400 font-mono">
                                            {Math.floor(segment.start_time / 60)}:{(segment.start_time % 60).toString().padStart(2, '0')}
                                          </span>
                                        </div>
                                        <p className="text-gray-700">{segment.text}</p>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Important Quotes */}
                          {topic.important_quotes?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                              {topic.important_quotes.map((quote, qidx) => (
                                <div key={qidx} className="bg-gray-50 p-3 rounded-lg border-l-4 border-purple-400">
                                  <p className="text-sm text-gray-700 italic">&quot;{quote.text}&quot;</p>
                                  <p className="text-xs text-gray-500 mt-1">— {quote.speaker}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Diğer Notlar (Parking Lot) - Collapsed by default */}
                    {analysis.topics?.some(t => t.name === "Diğer Notlar") && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <button
                          onClick={() => setShowParkingLot(prev => !prev)}
                          className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-purple-600 transition-colors w-full"
                        >
                          <span className={`transform transition-transform ${showParkingLot ? 'rotate-90' : ''}`}>▶</span>
                          📌 Diğer Notlar
                          <span className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded-full">
                            {analysis.topics.find(t => t.name === "Diğer Notlar")?.segment_ids?.length || 0} segment
                          </span>
                        </button>
                        
                        {showParkingLot && (
                          <div className="mt-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
                            <p className="text-sm text-gray-600 mb-3">
                              Ana konu başlıklarına dahil edilmeyen diğer konuşmalar:
                            </p>
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                              {analysis.topics
                                .find(t => t.name === "Diğer Notlar")
                                ?.segment_ids?.map((segIdx, sidx) => {
                                  const segment = transcript.segments[segIdx];
                                  if (!segment) return null;
                                  
                                  return (
                                    <div key={sidx} className="text-sm border-l-2 border-gray-300 pl-3 py-2 bg-white rounded">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="font-medium text-gray-700">{segment.speaker_name}</span>
                                        <span className="text-xs text-gray-400 font-mono">
                                          {Math.floor(segment.start_time / 60)}:{(segment.start_time % 60).toString().padStart(2, '0')}
                                        </span>
                                      </div>
                                      <p className="text-gray-600">{segment.text}</p>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Key Takeaways */}
                {analysis.key_takeaways?.length > 0 && (
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Target className="text-green-600" size={20} />
                      <h3 className="text-lg font-semibold text-gray-800">Ana Çıkarımlar</h3>
                    </div>
                    <ul className="space-y-2">
                      {analysis.key_takeaways.map((takeaway, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={16} />
                          <span className="font-medium">{takeaway}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Fallback - Show detailed summary if no organized content */}
                {(!analysis.organized_topics || analysis.organized_topics.length === 0) && analysis.detailed_summary && (
                  <div className="bg-gray-50 p-5 rounded-xl">
                    <h3 className="text-lg font-semibold mb-3">Detaylı Özet</h3>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{analysis.detailed_summary}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          {activeTab === "decisions" && analysis && (
            <ScrollArea className="panel-content">
              {analysis.decisions?.length === 0 ? (
                <div className="empty-state">
                  <p className="text-gray-500">Bu toplantıda karar tespit edilmedi</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {analysis.decisions?.map((decision, i) => (
                    <div key={i} className="item-card" data-testid={`decision-${i}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Target className="text-green-600" size={18} />
                            <span className="font-medium">{decision.text}</span>
                          </div>
                          {decision.evidence && (
                            <div className="evidence-quote">
                              &quot;{decision.evidence.quote}&quot;
                              <span className="block text-xs mt-1 text-gray-400">
                                — {decision.evidence.speaker}, {formatTimestamp(decision.evidence.timestamp)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          )}

          {activeTab === "actions" && analysis && (
            <ScrollArea className="panel-content">
              {analysis.action_items?.length === 0 ? (
                <div className="empty-state">
                  <p className="text-gray-500">Bu toplantıda aksiyon tespit edilmedi</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Summary Stats */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{analysis.action_items?.length || 0}</div>
                        <div className="text-xs text-gray-500">Toplam</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {tasks.filter(t => t.status === 'completed').length}
                        </div>
                        <div className="text-xs text-gray-500">Tamamlanan</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-500">
                          {tasks.filter(t => t.status !== 'completed').length}
                        </div>
                        <div className="text-xs text-gray-500">Bekleyen</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Items as Tasks */}
                  {analysis.action_items?.map((action, i) => {
                    const task = tasks.find(t => t.id === (action.id || `task-${i}`)) || {
                      id: action.id || `task-${i}`,
                      status: 'pending'
                    };
                    
                    return (
                      <div
                        key={i}
                        className={`p-4 rounded-xl border transition-all ${
                          task.status === 'completed' 
                            ? 'bg-green-50 border-green-200' 
                            : 'bg-white border-gray-200 hover:border-blue-300'
                        }`}
                        data-testid={`action-${i}`}
                      >
                        <div className="flex items-start gap-3">
                          <button
                            onClick={() => toggleTaskStatus(task.id)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 transition-colors flex-shrink-0 ${
                              task.status === 'completed'
                                ? 'bg-green-500 border-green-500 text-white'
                                : 'border-gray-300 hover:border-blue-500'
                            }`}
                          >
                            {task.status === 'completed' && <Check size={14} />}
                          </button>
                          
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-500' : ''}`}>
                              {action.task}
                            </p>
                            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                              {(action.assigned_to || action.owner) ? (
                                <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                                  <Users size={14} />
                                  {action.assigned_to || action.owner}
                                </span>
                              ) : (
                                <button
                                  onClick={() => {
                                    setSelectedActionIndex(i);
                                    setShowAssignDialog(true);
                                  }}
                                  className="flex items-center gap-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 px-2 py-0.5 rounded-full transition-colors"
                                >
                                  <UserPlus size={14} />
                                  Ata
                                </button>
                              )}
                              {action.deadline && (
                                <span className="flex items-center gap-1">
                                  <Clock size={14} />
                                  {action.deadline}
                                </span>
                              )}
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                action.priority === 'high' 
                                  ? 'bg-red-100 text-red-700'
                                  : action.priority === 'medium'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {action.priority === 'high' ? '🔴 Yüksek' : action.priority === 'medium' ? '🟡 Orta' : '🟢 Düşük'}
                              </span>
                            </div>
                            {action.exact_quote && (
                              <div className="evidence-quote mt-2">
                                &quot;{action.exact_quote}&quot;
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {actionTaskMap[i] ? (
                              <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                <ListChecks size={12} />
                                Task Eklendi
                              </span>
                            ) : (
                              <button
                                onClick={() => handleAddTaskFromAction(i)}
                                className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 px-2 py-1 rounded-full transition-colors"
                                title="Task Olarak Ekle"
                              >
                                <ListChecks size={12} />
                                Task Ekle
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          )}

          {/* Tasks Tab */}
          {activeTab === "tasks" && (
            <ScrollArea className="panel-content">
              {tasks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ListChecks size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>Henüz görev yok</p>
                  <p className="text-sm mt-2">Aksiyonlar otomatik olarak görevlere dönüştürülür</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Summary */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{tasks.length}</div>
                        <div className="text-xs text-gray-500">Toplam</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{tasks.filter(t => t.status === 'completed').length}</div>
                        <div className="text-xs text-gray-500">Tamamlanan</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-500">{tasks.filter(t => t.status !== 'completed').length}</div>
                        <div className="text-xs text-gray-500">Bekleyen</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Task List */}
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      className={`p-4 rounded-xl border transition-all ${
                        task.status === 'completed' 
                          ? 'bg-green-50 border-green-200' 
                          : 'bg-white border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => toggleTaskStatus(task.id)}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 transition-colors ${
                            task.status === 'completed'
                              ? 'bg-green-500 border-green-500 text-white'
                              : 'border-gray-300 hover:border-blue-500'
                          }`}
                        >
                          {task.status === 'completed' && <Check size={14} />}
                        </button>
                        <div className="flex-1">
                          <p className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-500' : ''}`}>
                            {task.task}
                          </p>
                          <div className="flex items-center gap-3 mt-2">
                            {task.owner && (
                              <span className="flex items-center gap-1 text-sm text-gray-500">
                                <User size={14} />
                                {task.owner}
                              </span>
                            )}
                            {task.deadline && (
                              <span className="flex items-center gap-1 text-sm text-gray-500">
                                <Calendar size={14} />
                                {task.deadline}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              task.priority === 'high' 
                                ? 'bg-red-100 text-red-700'
                                : task.priority === 'medium'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {task.priority === 'high' ? 'Yüksek' : task.priority === 'medium' ? 'Orta' : 'Düşük'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          )}
        </div>

        {/* Side Panel */}
        <div className="panel">
          <div className="panel-header">
            <h3 className="font-semibold text-gray-800">Toplantı Özeti</h3>
          </div>
          <ScrollArea className="panel-content">
            {analysis ? (
              <div className="space-y-6">
                {/* Executive Summary */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Özet</h4>
                  <p className="text-sm text-gray-700">
                    {analysis.analysis_failed
                      ? "Analiz oluşturulamadı — 'Yeniden Analiz Et' ile tekrar deneyin."
                      : (analysis.executive_summary || "Analiz bekleniyor...")}
                  </p>
                </div>

                {/* Decisions */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                    <CheckCircle className="text-green-600" size={16} />
                    Kararlar ({analysis.decisions?.length || 0})
                  </h4>
                  <ul className="space-y-2">
                    {analysis.decisions?.slice(0, 3).map((d, i) => (
                      <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-green-600 mt-1">•</span>
                        <span className="line-clamp-2">{d.text}</span>
                      </li>
                    ))}
                    {analysis.decisions?.length > 3 && (
                      <li
                        className="text-sm text-gray-500 cursor-pointer hover:text-gray-700"
                        onClick={() => setActiveTab("decisions")}
                      >
                        +{analysis.decisions.length - 3} daha fazla...
                      </li>
                    )}
                  </ul>
                </div>

                {/* Actions */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                    <ListChecks className="text-orange-600" size={16} />
                    Aksiyonlar ({analysis.action_items?.length || 0})
                  </h4>
                  <ul className="space-y-2">
                    {analysis.action_items?.slice(0, 3).map((a, i) => (
                      <li key={i} className="text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          <input type="checkbox" className="w-4 h-4 rounded" readOnly />
                          <span className="line-clamp-1">{a.task}</span>
                        </div>
                        {a.owner && (
                          <span className="text-xs text-gray-500 ml-6">{a.owner}</span>
                        )}
                      </li>
                    ))}
                    {analysis.action_items?.length > 3 && (
                      <li
                        className="text-sm text-gray-500 cursor-pointer hover:text-gray-700"
                        onClick={() => setActiveTab("actions")}
                      >
                        +{analysis.action_items.length - 3} daha fazla...
                      </li>
                    )}
                  </ul>
                </div>

                {/* Speakers */}
                {transcript?.speakers && Object.keys(transcript.speakers).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Konuşmacılar</h4>
                    <div className="space-y-2">
                      {Object.entries(transcript.speakers).map(([id, speaker]) => (
                        <div key={id} className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: speaker.color }}
                          />
                          <span className="text-sm">{speaker.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <Skeleton className="h-20" />
                <Skeleton className="h-32" />
                <Skeleton className="h-32" />
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
      )}

      {/* Email Dialog */}
      {showEmailDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowEmailDialog(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Toplantı Notunu E-posta ile Gönder</h3>
              <button
                onClick={() => setShowEmailDialog(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alıcı E-posta Adresleri *
                </label>
                <Input
                  type="text"
                  placeholder="ornek@mail.com, ornek2@mail.com"
                  value={emailRecipients}
                  onChange={(e) => setEmailRecipients(e.target.value)}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Birden fazla adres için virgül (,) kullanın</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Konu (Opsiyonel)
                </label>
                <Input
                  type="text"
                  placeholder={`Toplantı Notu: ${meeting?.title || 'Toplantı'}`}
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mesaj (Opsiyonel)
                </label>
                <textarea
                  placeholder="E-posta ile birlikte göndermek istediğiniz ek mesajı buraya yazın..."
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  rows="4"
                />
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <FileText className="text-gray-600" size={18} />
                <span className="text-sm text-gray-700">Not: MoM e-posta içeriği olarak gönderilecek</span>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowEmailDialog(false)}
                className="flex-1"
                disabled={isSendingEmail}
              >
                İptal
              </Button>
              <Button
                onClick={handleSendEmail}
                className="flex-1"
                disabled={isSendingEmail}
              >
                {isSendingEmail ? (
                  <>
                    <RefreshCw size={16} className="mr-2 animate-spin" />
                    Gönderiliyor...
                  </>
                ) : (
                  <>
                    <Mail size={16} className="mr-2" />
                    Gönder
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Task Assignment Dialog */}
      {showAssignDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAssignDialog(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Görevi Ata</h3>
              <button
                onClick={() => setShowAssignDialog(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            {selectedActionIndex !== null && analysis?.action_items?.[selectedActionIndex] && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-900 font-medium">
                  {analysis.action_items[selectedActionIndex].task}
                </p>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kişi Adı *
                </label>
                <Input
                  type="text"
                  placeholder="Örn: Ahmet Yılmaz"
                  value={assigneeName}
                  onChange={(e) => setAssigneeName(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-posta (Opsiyonel)
                </label>
                <Input
                  type="email"
                  placeholder="ahmet@example.com"
                  value={assigneeEmail}
                  onChange={(e) => setAssigneeEmail(e.target.value)}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Bildirim göndermek için e-posta ekleyebilirsiniz</p>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAssignDialog(false)}
                className="flex-1"
                disabled={isAssigning}
              >
                İptal
              </Button>
              <Button
                onClick={handleAssignTask}
                className="flex-1"
                disabled={isAssigning}
              >
                {isAssigning ? (
                  <>
                    <RefreshCw size={16} className="mr-2 animate-spin" />
                    Atanıyor...
                  </>
                ) : (
                  <>
                    <UserPlus size={16} className="mr-2" />
                    Ata
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Audio Player */}
    </div>
  );
};

// ============== ANALYTICS PAGE ==============
const AnalyticsPage = () => {
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [speakers, setSpeakers] = useState(null);
  const [decisionsActions, setDecisionsActions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [overviewData, trendsData, speakersData, daData] = await Promise.all([
          getAnalyticsOverview(),
          getAnalyticsTrends(),
          getSpeakerAnalytics(),
          getDecisionsActionsAnalytics()
        ]);
        setOverview(overviewData);
        setTrends(trendsData);
        setSpeakers(speakersData);
        setDecisionsActions(daData);
      } catch (error) {
        console.error("Failed to fetch analytics:", error);
        toast.error("Analitik verileri yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="main-content" data-testid="analytics-page">
        <div className="p-8">
          <h1 className="text-2xl font-semibold mb-6">Analitik</h1>
          <div className="grid grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
          <Skeleton className="h-64 rounded-2xl mb-8" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-48 rounded-2xl" />
            <Skeleton className="h-48 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content" data-testid="analytics-page">
      <div className="p-8">
        <h1 className="text-2xl font-semibold mb-6">Analitik</h1>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="card-main p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Toplam Toplantı</p>
                <p className="text-3xl font-bold mt-1">{overview?.total_meetings || 0}</p>
                <p className="text-xs text-green-600 mt-1">
                  Bu ay: {overview?.meetings_this_month || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center">
                <Calendar className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="card-main p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Toplam Karar</p>
                <p className="text-3xl font-bold mt-1">{overview?.total_decisions || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center">
                <Target className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="card-main p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Aksiyon Planları</p>
                <p className="text-3xl font-bold mt-1">{overview?.total_actions || 0}</p>
                <p className="text-xs text-orange-600 mt-1">
                  Bekleyen: {overview?.pending_actions || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-orange-50 flex items-center justify-center">
                <ListChecks className="text-orange-600" size={24} />
              </div>
            </div>
          </div>

          <div className="card-main p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Toplam Süre</p>
                <p className="text-3xl font-bold mt-1">{overview?.total_duration_hours || 0}sa</p>
                <p className="text-xs text-gray-500 mt-1">
                  Ort: {overview?.avg_duration_minutes || 0} dk
                </p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center">
                <Clock className="text-purple-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          {/* Weekly Meeting Trend */}
          <div className="card-main p-6">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <TrendingUp className="text-blue-600" size={20} />
              Haftalık Toplantı Trendi
            </h3>
            <div className="h-48 flex items-end gap-2">
              {trends?.weekly_meetings?.slice(-8).map((week, i) => {
                const maxCount = Math.max(...(trends?.weekly_meetings?.map(w => w.count) || [1]));
                const height = maxCount > 0 ? (week.count / maxCount) * 100 : 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-blue-500 rounded-t-lg transition-all hover:bg-blue-600"
                      style={{ height: `${Math.max(height, 5)}%` }}
                      title={`${week.count} toplantı`}
                    />
                    <span className="text-xs text-gray-500 mt-2 truncate w-full text-center">
                      W{week.week?.split('-W')[1]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Platform Distribution */}
          <div className="card-main p-6">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <PieChart className="text-green-600" size={20} />
              Platform Dağılımı
            </h3>
            <div className="space-y-3">
              {trends?.platform_distribution?.map((platform, i) => {
                const total = trends?.platform_distribution?.reduce((a, p) => a + p.count, 0) || 1;
                const percent = Math.round((platform.count / total) * 100);
                const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500'];
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize">{platform.platform || 'Diğer'}</span>
                      <span className="text-gray-500">{platform.count} ({percent}%)</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${colors[i % colors.length]} rounded-full`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {(!trends?.platform_distribution || trends.platform_distribution.length === 0) && (
                <p className="text-gray-500 text-sm">Henüz veri yok</p>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-2 gap-6">
          {/* Top Speakers */}
          <div className="card-main p-6">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Mic className="text-indigo-600" size={20} />
              En Aktif Konuşmacılar
            </h3>
            <div className="space-y-3">
              {speakers?.speakers?.slice(0, 5).map((speaker, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium">
                      {speaker.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <span className="text-sm">{speaker.name}</span>
                  </div>
                  <Badge variant="secondary">{speaker.meeting_count} toplantı</Badge>
                </div>
              ))}
              {(!speakers?.speakers || speakers.speakers.length === 0) && (
                <p className="text-gray-500 text-sm">Henüz veri yok</p>
              )}
            </div>
          </div>

          {/* Decisions & Actions */}
          <div className="card-main p-6">
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <BarChart3 className="text-orange-600" size={20} />
              Karar & Aksiyon Dağılımı
            </h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-2">Aksiyon Öncelikleri</p>
                <div className="flex gap-2">
                  {decisionsActions?.action_priorities?.map((p, i) => {
                    const colors = {
                      high: 'bg-red-100 text-red-700',
                      medium: 'bg-yellow-100 text-yellow-700',
                      low: 'bg-green-100 text-green-700'
                    };
                    return (
                      <Badge key={i} className={colors[p.priority] || 'bg-gray-100'}>
                        {p.priority === 'high' ? 'Yüksek' : p.priority === 'medium' ? 'Orta' : 'Düşük'}: {p.count}
                      </Badge>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">Aksiyon Durumları</p>
                <div className="flex gap-2">
                  {decisionsActions?.action_statuses?.map((s, i) => {
                    const colors = {
                      pending: 'bg-yellow-100 text-yellow-700',
                      completed: 'bg-green-100 text-green-700',
                      in_progress: 'bg-blue-100 text-blue-700'
                    };
                    const labels = {
                      pending: 'Bekliyor',
                      completed: 'Tamamlandı',
                      in_progress: 'Devam Ediyor'
                    };
                    return (
                      <Badge key={i} className={colors[s.status] || 'bg-gray-100'}>
                        {labels[s.status] || s.status}: {s.count}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== LOGIN PAGE (WeTransfer Style Magic Code) ==============
const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken, isAuthenticated } = useAuth();
  const [step, setStep] = useState("email"); // email, code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [countdown, setCountdown] = useState(0);
  const codeInputRefs = [
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
  ];

  // Check for Google OAuth callback
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      const sessionId = hash.split("session_id=")[1]?.split("&")[0];
      if (sessionId) {
        handleGoogleCallback(sessionId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleGoogleCallback = async (sessionId) => {
    setLoading(true);
    try {
      const response = await googleAuthCallback(sessionId);
      loginWithToken(response.access_token, response.user);
      toast.success("Google ile giriş başarılı!");
      // Clear hash from URL
      window.history.replaceState(null, "", window.location.pathname);
      navigate('/');
    } catch (error) {
      toast.error("Google ile giriş başarısız");
      window.history.replaceState(null, "", window.location.pathname);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const validateEmail = () => {
    if (!email) {
      setErrors({ email: "E-posta adresi gerekli" });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ email: "Geçerli bir e-posta adresi girin" });
      return false;
    }
    return true;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail()) return;

    setLoading(true);
    setErrors({});
    try {
      await initiateAuth(email.trim());
      toast.success("Doğrulama kodu gönderildi!");
      setStep("code");
      setCountdown(60);
      // Focus first code input
      setTimeout(() => codeInputRefs[0].current?.focus(), 100);
    } catch (error) {
      const msg = error.response?.data?.detail || "Kod gönderilemedi";
      setErrors({ general: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5 && newCode.every(d => d)) {
      handleCodeSubmit(newCode.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      codeInputRefs[index - 1].current?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      codeInputRefs[index - 1].current?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      codeInputRefs[index + 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split("");
      setCode(newCode);
      handleCodeSubmit(pastedData);
    }
  };

  const handleCodeSubmit = async (fullCode = null) => {
    const codeStr = fullCode || code.join("");
    if (codeStr.length !== 6) {
      setErrors({ code: "6 haneli kodu girin" });
      return;
    }

    setLoading(true);
    setErrors({});
    try {
      const response = await verifyAuth(email.trim(), codeStr);
      loginWithToken(response.access_token, response.user);
      toast.success("Giriş başarılı!");
      navigate('/');
    } catch (error) {
      const msg = error.response?.data?.detail || "Geçersiz kod";
      setErrors({ code: msg });
      // Clear code on error
      setCode(["", "", "", "", "", ""]);
      codeInputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    
    setResendLoading(true);
    try {
      await resendCode(email.trim());
      toast.success("Yeni kod gönderildi!");
      setCountdown(60);
      setCode(["", "", "", "", "", ""]);
      codeInputRefs[0].current?.focus();
    } catch (error) {
      toast.error("Kod gönderilemedi");
    } finally {
      setResendLoading(false);
    }
  };

  // Show loading while processing Google callback
  if (loading && window.location.hash.includes("session_id")) {
    return (
      <div className="auth-page" data-testid="login-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <div className="auth-logo">
                <img src="/notellio-text-trimmed.png" alt="Notellio" className="notellio-logo-auth" />
              </div>
              <h1>Giriş Yapılıyor...</h1>
              <div className="flex justify-center mt-4">
                <div className="spinner-large" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page" data-testid="login-page">
      <div className="auth-container">
        <div className="auth-card">
          {/* Logo & Header */}
          <div className="auth-header">
            <div className="auth-logo">
              <img src="/logo.png" alt="Notellio" style={{height: '64px'}} />
            </div>
            <h1>{step === "email" ? "Hoş Geldiniz" : "Kodu Girin"}</h1>
            <p>
              {step === "email" 
                ? "E-posta adresinizle devam edin" 
                : `${email} adresine gönderilen 6 haneli kodu girin`}
            </p>
          </div>

          {/* Error Message */}
          {errors.general && (
            <div className="auth-error" data-testid="login-error">
              <AlertCircle size={16} />
              <span>{errors.general}</span>
            </div>
          )}

          {/* Step: Email */}
          {step === "email" && (
            <>
              <form onSubmit={handleEmailSubmit} className="auth-form">
                <div className="form-group">
                  <label>E-posta Adresi</label>
                  <div className={`input-with-icon ${errors.email ? 'error' : ''}`}>
                    <Mail size={18} className="input-icon" />
                    <Input
                      type="email"
                      placeholder="ornek@sirket.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (errors.email) setErrors({});
                      }}
                      data-testid="login-email"
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </div>

                <Button
                  type="submit"
                  className="auth-button"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <div className="spinner" />
                      Gönderiliyor...
                    </>
                  ) : (
                    "Devam Et"
                  )}
                </Button>
              </form>

              {/* Divider - Hidden while Google login is disabled */}
              {/* <div className="auth-divider">
                <span>veya</span>
              </div> */}

              {/* Google Login - Temporarily disabled */}
              {/* 
              <button
                type="button"
                className="google-button"
                onClick={handleGoogleLogin}
                data-testid="google-login"
              >
                <svg className="google-icon" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google ile Devam Et
              </button>
              */}
            </>
          )}

          {/* Step: Code */}
          {step === "code" && (
            <>
              <div className="code-input-container">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={codeInputRefs[index]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className={`code-input ${errors.code ? 'error' : ''}`}
                    data-testid={`code-input-${index}`}
                    autoFocus={index === 0}
                  />
                ))}
              </div>
              {errors.code && <div className="code-error">{errors.code}</div>}

              <Button
                type="button"
                className="auth-button"
                disabled={loading || code.some(d => !d)}
                onClick={() => handleCodeSubmit()}
                data-testid="verify-code"
              >
                {loading ? (
                  <>
                    <div className="spinner" />
                    Doğrulanıyor...
                  </>
                ) : (
                  "Giriş Yap"
                )}
              </Button>

              <div className="resend-section">
                <p className="text-gray-500 text-sm">Kod gelmedi mi?</p>
                <button
                  type="button"
                  className="resend-button"
                  onClick={handleResend}
                  disabled={countdown > 0 || resendLoading}
                >
                  {resendLoading ? (
                    "Gönderiliyor..."
                  ) : countdown > 0 ? (
                    `Tekrar gönder (${countdown}s)`
                  ) : (
                    "Tekrar Gönder"
                  )}
                </button>
              </div>

              <button
                type="button"
                className="back-to-email"
                onClick={() => {
                  setStep("email");
                  setCode(["", "", "", "", "", ""]);
                  setErrors({});
                }}
                data-testid="back-to-email"
              >
                <ChevronLeft size={16} />
                Farklı e-posta kullan
              </button>
            </>
          )}

          {/* Footer */}
          {step === "email" && (
            <div className="auth-footer">
              <p className="text-sm text-gray-500">
                Devam ederek{" "}
                <a href="#" className="auth-link" onClick={(e) => { e.preventDefault(); toast.info("Kullanım şartları yakında!"); }}>Kullanım Şartlarını</a>{" "}
                kabul etmiş olursunuz
              </p>
            </div>
          )}
        </div>

        {/* Trust Badges */}
        <div className="auth-trust">
          <span>🔒 256-bit SSL şifreleme</span>
          <span>•</span>
          <span>KVKK uyumlu</span>
        </div>
      </div>
    </div>
  );
};

// ============== REGISTER PAGE (Redirects to Login - Magic Code handles both) ==============
const RegisterPage = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Magic code auth handles both login and registration
    // Redirect to login page
    navigate('/login');
  }, [navigate]);

  return null;
};

// ============== SETTINGS PAGE ==============
const SettingsPage = () => {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [company, setCompany] = useState(user?.company || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Bot/Assistant Settings
  const [botName, setBotName] = useState(user?.settings?.bot_name || "Notellio Asistan");
  const [botAvatarUrl, setBotAvatarUrl] = useState(user?.settings?.bot_avatar_url || "");
  const [botEntryMessage, setBotEntryMessage] = useState(user?.settings?.bot_entry_message || "");
  const [botAutoLeave, setBotAutoLeave] = useState(user?.settings?.bot_auto_leave ?? true);
  const [botLoading, setBotLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = React.useRef(null);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await updateProfile({ name, company });
      updateUser(response.user);
      toast.success("Profil güncellendi");
    } catch (error) {
      toast.error("Güncelleme başarısız");
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error("Sadece PNG, JPG veya WebP formatları desteklenir");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Dosya boyutu 2MB'dan küçük olmalı");
      return;
    }

    setAvatarUploading(true);
    try {
      const response = await uploadBotAvatar(file);
      setBotAvatarUrl(response.avatar_url);
      toast.success("Avatar yüklendi");
      
      // Update user in context
      const updatedSettings = { ...user?.settings, bot_avatar_url: response.avatar_url };
      updateUser({ ...user, settings: updatedSettings });
    } catch (error) {
      const msg = error.response?.data?.detail || "Avatar yüklenemedi";
      toast.error(msg);
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAvatar = async () => {
    if (!botAvatarUrl) return;
    
    if (!window.confirm("Avatarı silmek istediğinizden emin misiniz?")) return;

    setAvatarUploading(true);
    try {
      await deleteBotAvatar();
      setBotAvatarUrl("");
      toast.success("Avatar silindi");
      
      // Update user in context
      const updatedSettings = { ...user?.settings };
      delete updatedSettings.bot_avatar_url;
      delete updatedSettings.bot_avatar_filename;
      updateUser({ ...user, settings: updatedSettings });
    } catch (error) {
      toast.error("Avatar silinemedi");
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleUpdateBotSettings = async (e) => {
    e.preventDefault();
    setBotLoading(true);
    try {
      const response = await updateProfile({ 
        settings: {
          ...user?.settings,
          bot_name: botName,
          bot_avatar_url: botAvatarUrl,
          bot_entry_message: botEntryMessage,
          bot_auto_leave: botAutoLeave
        }
      });
      updateUser(response.user);
      toast.success("Asistan ayarları güncellendi");
    } catch (error) {
      toast.error("Güncelleme başarısız");
    } finally {
      setBotLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      toast.error("Mevcut ve yeni şifre gerekli");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Yeni şifre en az 6 karakter olmalı");
      return;
    }
    
    setPasswordLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success("Şifre güncellendi");
      setCurrentPassword("");
      setNewPassword("");
    } catch (error) {
      const msg = error.response?.data?.detail || "Şifre güncellenemedi";
      toast.error(msg);
    } finally {
      setPasswordLoading(false);
    }
  };

  // Get full avatar URL
  const getAvatarUrl = () => {
    if (!botAvatarUrl) return null;
    if (botAvatarUrl.startsWith('http')) return botAvatarUrl;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}${botAvatarUrl}`;
  };

  return (
    <div className="main-content" data-testid="settings-page">
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-2xl font-semibold mb-6">Ayarlar</h1>
        
        {/* Profile Settings */}
        <div className="card-main p-6 mb-6">
          <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
            <User size={20} className="text-blue-600" />
            Profil Bilgileri
          </h2>
          
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Ad Soyad</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ad Soyad"
                data-testid="settings-name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">E-posta</label>
              <Input
                value={user?.email || ""}
                disabled
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">E-posta değiştirilemez</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Şirket</label>
              <Input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Şirket adı"
                data-testid="settings-company"
              />
            </div>
            
            <Button type="submit" disabled={loading} data-testid="settings-save">
              {loading ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </form>
        </div>

        {/* Bot/Assistant Settings */}
        <div className="card-main p-6 mb-6">
          <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
            <Bot size={20} className="text-purple-600" />
            Asistan Özelleştirme
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Toplantılara katılan asistanınızı kişiselleştirin
          </p>
          
          <form onSubmit={handleUpdateBotSettings} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Asistan Adı</label>
              <Input
                value={botName}
                onChange={(e) => setBotName(e.target.value)}
                placeholder="Notellio Asistan"
                maxLength={50}
                data-testid="settings-bot-name"
              />
              <p className="text-xs text-gray-500 mt-1">Toplantıda görünecek isim (max 50 karakter)</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Asistan Avatarı</label>
              
              {/* Avatar Preview */}
              <div className="flex items-center gap-4 mb-3">
                <div className="relative">
                  {botAvatarUrl ? (
                    <img 
                      src={getAvatarUrl()} 
                      alt="Bot Avatar" 
                      className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%239333ea"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/></svg>';
                      }}
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center">
                      <Bot size={32} className="text-purple-600" />
                    </div>
                  )}
                  
                  {botAvatarUrl && (
                    <button
                      type="button"
                      onClick={handleDeleteAvatar}
                      disabled={avatarUploading}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      title="Avatarı Sil"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                
                <div className="flex-1">
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    id="avatar-upload"
                    data-testid="settings-bot-avatar-input"
                  />
                  <label
                    htmlFor="avatar-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 border border-purple-200 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors ${avatarUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {avatarUploading ? (
                      <>
                        <RefreshCw size={16} className="animate-spin text-purple-600" />
                        <span className="text-sm text-purple-600">Yükleniyor...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="text-purple-600" />
                        <span className="text-sm text-purple-600">
                          {botAvatarUrl ? "Avatarı Değiştir" : "Avatar Yükle"}
                        </span>
                      </>
                    )}
                  </label>
                  <p className="text-xs text-gray-500 mt-2">PNG, JPG veya WebP, max 2MB, 256x256 önerilir</p>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Giriş Mesajı (Opsiyonel)</label>
              <textarea
                value={botEntryMessage}
                onChange={(e) => setBotEntryMessage(e.target.value)}
                placeholder="Merhaba! Bu toplantı Notellio tarafından kayıt edilmektedir. KVKK kapsamında bilgileriniz güvenle saklanacaktır."
                className="w-full px-3 py-2 border rounded-lg text-sm min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength={500}
                data-testid="settings-bot-message"
              />
              <p className="text-xs text-gray-500 mt-1">Asistan toplantıya katıldığında chat'e gönderilir (max 500 karakter)</p>
            </div>
            
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="botAutoLeave"
                checked={botAutoLeave}
                onChange={(e) => setBotAutoLeave(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
                data-testid="settings-bot-auto-leave"
              />
              <label htmlFor="botAutoLeave" className="text-sm">
                Son katılımcı ayrıldığında asistanı otomatik çıkar
              </label>
            </div>
            
            <Button type="submit" disabled={botLoading} className="bg-purple-600 hover:bg-purple-700" data-testid="settings-bot-save">
              {botLoading ? "Kaydediliyor..." : "Asistan Ayarlarını Kaydet"}
            </Button>
          </form>
        </div>
        
        {/* Password Change - Only for password-based users */}
        {user?.auth_method !== 'magic_code' && user?.auth_method !== 'google' && (
        <div className="card-main p-6 mb-6">
          <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
            <Lock size={20} className="text-orange-600" />
            Şifre Değiştir
          </h2>
          
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Mevcut Şifre</label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="settings-current-password"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Yeni Şifre</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="settings-new-password"
              />
              <p className="text-xs text-gray-500 mt-1">En az 6 karakter</p>
            </div>
            
            <Button type="submit" variant="outline" disabled={passwordLoading} data-testid="settings-change-password">
              {passwordLoading ? "Değiştiriliyor..." : "Şifreyi Değiştir"}
            </Button>
          </form>
        </div>
        )}

        {/* Auth Method Info */}
        {(user?.auth_method === 'magic_code' || user?.auth_method === 'google') && (
        <div className="card-main p-6">
          <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
            <Mail size={20} className="text-green-600" />
            Giriş Yöntemi
          </h2>
          <p className="text-gray-600">
            {user?.auth_method === 'google' 
              ? 'Google hesabınızla giriş yapıyorsunuz.' 
              : 'E-posta doğrulama kodu ile giriş yapıyorsunuz.'
            }
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Her giriş yaptığınızda e-posta adresinize bir doğrulama kodu gönderilir.
          </p>
        </div>
        )}
      </div>
    </div>
  );
};

// ============== TASKS PAGE ==============
const TasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterPriority, setFilterPriority] = useState(null);
  const [filterTag, setFilterTag] = useState(null);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  // Task form state
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [taskAssignee, setTaskAssignee] = useState("");
  const [taskAssigneeEmail, setTaskAssigneeEmail] = useState("");
  const [taskPriority, setTaskPriority] = useState("medium");
  const [taskDeadline, setTaskDeadline] = useState("");
  const [taskTags, setTaskTags] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#3B82F6");
  const [taskReminderEnabled, setTaskReminderEnabled] = useState(false);
  const [taskReminderDays, setTaskReminderDays] = useState(1);
  
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  
  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await getTasks(filterStatus, filterPriority, filterTag);
      setTasks(data.tasks || []);
    } catch (error) {
      console.error("Failed to load tasks:", error);
      toast.error("Task'lar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadTasks();
  }, [filterStatus, filterPriority, filterTag]);
  
  const handleToggleComplete = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await updateTask(task.id, { status: newStatus });
      toast.success(newStatus === 'completed' ? 'Task tamamlandı!' : 'Task yeniden açıldı');
      loadTasks();
    } catch (error) {
      toast.error("Task güncellenemedi");
    }
  };
  
  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Bu task'ı silmek istediğinizden emin misiniz?")) return;
    
    try {
      await deleteTask(taskId);
      toast.success("Task silindi");
      loadTasks();
    } catch (error) {
      toast.error("Task silinemedi");
    }
  };
  
  const openTaskDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      setTaskTitle(task.title);
      setTaskDescription(task.description || "");
      setTaskAssignee(task.assigned_to || "");
      setTaskAssigneeEmail(task.assigned_to_email || "");
      setTaskPriority(task.priority);
      setTaskDeadline(task.deadline ? task.deadline.split('T')[0] : "");
      setTaskTags(task.tags || []);
      setTaskReminderEnabled(task.reminder?.enabled || false);
      setTaskReminderDays(task.reminder?.days_before || 1);
    } else {
      setEditingTask(null);
      setTaskTitle("");
      setTaskDescription("");
      setTaskAssignee("");
      setTaskAssigneeEmail("");
      setTaskPriority("medium");
      setTaskDeadline("");
      setTaskTags([]);
      setTaskReminderEnabled(false);
      setTaskReminderDays(1);
    }
    setShowTaskDialog(true);
  };
  
  const handleAddTag = () => {
    if (!newTagName.trim()) return;
    
    const newTag = {
      name: newTagName.trim(),
      color: newTagColor
    };
    
    setTaskTags([...taskTags, newTag]);
    setNewTagName("");
    setNewTagColor("#3B82F6");
  };
  
  const handleRemoveTag = (index) => {
    setTaskTags(taskTags.filter((_, i) => i !== index));
  };
  
  const handleSaveTask = async () => {
    if (!taskTitle.trim()) {
      toast.error("Task başlığı gerekli");
      return;
    }
    
    setIsSaving(true);
    try {
      const taskData = {
        title: taskTitle,
        description: taskDescription || null,
        assigned_to: taskAssignee || null,
        assigned_to_email: taskAssigneeEmail || null,
        priority: taskPriority,
        deadline: taskDeadline || null,
        tags: taskTags,
        reminder_enabled: taskReminderEnabled,
        reminder_days_before: taskReminderEnabled ? taskReminderDays : null
      };
      
      if (editingTask) {
        await updateTask(editingTask.id, taskData);
        toast.success("Task güncellendi");
      } else {
        await createTask(taskData);
        toast.success("Task oluşturuldu");
      }
      
      setShowTaskDialog(false);
      loadTasks();
    } catch (error) {
      toast.error(editingTask ? "Task güncellenemedi" : "Task oluşturulamadı");
    } finally {
      setIsSaving(false);
    }
  };
  
  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length
  };
  
  // Get unique tags from all tasks
  const allTags = [...new Set(tasks.flatMap(t => t.tags?.map(tag => tag.name) || []))];
  
  return (
    <div className="page-container animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasklar</h1>
          <p className="text-gray-500 text-sm mt-1">Tüm görevlerinizi tek yerden yönetin</p>
        </div>
        <Button onClick={() => openTaskDialog()} className="gap-2">
          <ListChecks size={18} />
          Yeni Task
        </Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card-main p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-500">Toplam</div>
        </div>
        <div className="card-main p-4">
          <div className="text-2xl font-bold text-orange-600">{stats.pending}</div>
          <div className="text-sm text-gray-500">Bekleyen</div>
        </div>
        <div className="card-main p-4">
          <div className="text-2xl font-bold text-blue-600">{stats.in_progress}</div>
          <div className="text-sm text-gray-500">Devam Eden</div>
        </div>
        <div className="card-main p-4">
          <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
          <div className="text-sm text-gray-500">Tamamlanan</div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="card-main p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          <select 
            value={filterStatus || ""}
            onChange={(e) => setFilterStatus(e.target.value || null)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tüm Durumlar</option>
            <option value="pending">Bekleyen</option>
            <option value="in_progress">Devam Eden</option>
            <option value="completed">Tamamlanan</option>
          </select>
          
          <select
            value={filterPriority || ""}
            onChange={(e) => setFilterPriority(e.target.value || null)}
            className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tüm Öncelikler</option>
            <option value="high">Yüksek</option>
            <option value="medium">Orta</option>
            <option value="low">Düşük</option>
          </select>
          
          {allTags.length > 0 && (
            <select
              value={filterTag || ""}
              onChange={(e) => setFilterTag(e.target.value || null)}
              className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tüm Etiketler</option>
              {allTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          )}
          
          {(filterStatus || filterPriority || filterTag) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilterStatus(null);
                setFilterPriority(null);
                setFilterTag(null);
              }}
            >
              Filtreleri Temizle
            </Button>
          )}
        </div>
      </div>
      
      {/* Task List */}
      {loading ? (
        <div className="card-main p-12">
          <div className="flex justify-center">
            <RefreshCw className="animate-spin text-gray-400" size={32} />
          </div>
        </div>
      ) : tasks.length === 0 ? (
        <div className="card-main p-12 text-center">
          <ListChecks size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-4">
            {filterStatus || filterPriority || filterTag 
              ? "Bu filtreyle eşleşen task bulunamadı" 
              : "Henüz task yok"}
          </p>
          <Button onClick={() => openTaskDialog()}>
            {filterStatus || filterPriority || filterTag ? "Yeni Task Oluştur" : "İlk Task'ı Oluştur"}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map(task => (
            <div
              key={task.id}
              className={`card-main p-4 hover:shadow-md transition-all ${
                task.status === 'completed' ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <button
                  onClick={() => handleToggleComplete(task)}
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 transition-colors ${
                    task.status === 'completed'
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'border-gray-300 hover:border-blue-500'
                  }`}
                >
                  {task.status === 'completed' && <Check size={14} />}
                </button>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-medium text-gray-900 ${task.status === 'completed' ? 'line-through' : ''}`}>
                        {task.title}
                      </h3>
                      {task.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{task.description}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openTaskDialog(task)}
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                        title="Düzenle"
                      >
                        <Settings size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteTask(task.id)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                        title="Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {task.assigned_to && (
                      <span className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                        <Users size={12} />
                        {task.assigned_to}
                      </span>
                    )}
                    
                    {task.deadline && (
                      <span className="flex items-center gap-1 text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-full">
                        <Calendar size={12} />
                        {new Date(task.deadline).toLocaleDateString('tr-TR')}
                      </span>
                    )}
                    
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      task.priority === 'high'
                        ? 'bg-red-100 text-red-700'
                        : task.priority === 'medium'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {task.priority === 'high' ? '🔴 Yüksek' : task.priority === 'medium' ? '🟡 Orta' : '🟢 Düşük'}
                    </span>
                    
                    {task.tags?.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: tag.color + '20', color: tag.color }}
                      >
                        #{tag.name}
                      </span>
                    ))}
                    
                    {task.reminder?.enabled && (
                      <span className="flex items-center gap-1 text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded-full">
                        <Bell size={12} />
                        Hatırlatma
                      </span>
                    )}
                    
                    {task.source_meeting_id && (
                      <button
                        onClick={() => navigate(`/meeting/${task.source_meeting_id}`)}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors"
                      >
                        <Link2 size={12} />
                        Toplantıya Git
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Task Dialog */}
      {showTaskDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowTaskDialog(false)}>
          <div 
            className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingTask ? "Task'ı Düzenle" : "Yeni Task Oluştur"}
              </h3>
              <button
                onClick={() => setShowTaskDialog(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Başlık *
                </label>
                <Input
                  type="text"
                  placeholder="Task başlığı..."
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  className="w-full"
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama
                </label>
                <textarea
                  placeholder="Task açıklaması..."
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Assignee */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sorumlu Kişi
                  </label>
                  <Input
                    type="text"
                    placeholder="İsim"
                    value={taskAssignee}
                    onChange={(e) => setTaskAssignee(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-posta
                  </label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={taskAssigneeEmail}
                    onChange={(e) => setTaskAssigneeEmail(e.target.value)}
                  />
                </div>
              </div>
              
              {/* Priority & Deadline */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Öncelik
                  </label>
                  <select
                    value={taskPriority}
                    onChange={(e) => setTaskPriority(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Düşük</option>
                    <option value="medium">Orta</option>
                    <option value="high">Yüksek</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Deadline
                  </label>
                  <Input
                    type="date"
                    value={taskDeadline}
                    onChange={(e) => setTaskDeadline(e.target.value)}
                  />
                </div>
              </div>
              
              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Etiketler
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {taskTags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: tag.color + '20', color: tag.color }}
                    >
                      #{tag.name}
                      <button
                        onClick={() => handleRemoveTag(idx)}
                        className="hover:opacity-70"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Etiket adı"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    className="flex-1"
                  />
                  <input
                    type="color"
                    value={newTagColor}
                    onChange={(e) => setNewTagColor(e.target.value)}
                    className="w-12 h-10 border rounded-lg cursor-pointer"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddTag}
                    disabled={!newTagName.trim()}
                  >
                    Ekle
                  </Button>
                </div>
              </div>
              
              {/* Reminder */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <input
                    type="checkbox"
                    id="reminderEnabled"
                    checked={taskReminderEnabled}
                    onChange={(e) => setTaskReminderEnabled(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <label htmlFor="reminderEnabled" className="text-sm font-medium text-gray-700">
                    Hatırlatma Aktif
                  </label>
                </div>
                {taskReminderEnabled && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      Deadline'dan kaç gün önce hatırlatılsın?
                    </label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={taskReminderDays}
                      onChange={(e) => setTaskReminderDays(parseInt(e.target.value))}
                      className="w-32"
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowTaskDialog(false)}
                className="flex-1"
                disabled={isSaving}
              >
                İptal
              </Button>
              <Button
                onClick={handleSaveTask}
                className="flex-1"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <RefreshCw size={16} className="mr-2 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Check size={16} className="mr-2" />
                    {editingTask ? "Güncelle" : "Oluştur"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== MAIN APP ==============
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Header />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            
            {/* Protected routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/analytics" element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/tasks" element={
              <ProtectedRoute>
                <TasksPage />
              </ProtectedRoute>
            } />
            <Route path="/meeting/:meetingId" element={
              <ProtectedRoute>
                <MeetingDetail />
              </ProtectedRoute>
            } />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
